import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Sprout, Droplets, Droplet, Thermometer, BatteryMedium, RefreshCw,
    CloudRain, Wifi, WifiOff, Play, Square, Save, Gauge, Wind,
} from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ReferenceArea,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const POLL_MS = 3000;
const n1 = (v, s = '') => (v === null || v === undefined ? '--' : `${Number(v).toFixed(1)}${s}`);
const n0 = (v, s = '') => (v === null || v === undefined ? '--' : `${Math.round(v)}${s}`);

// couleur (hex) selon l'humidité du sol
function moistColor(v, s) {
    if (v === null || v === undefined) return '#94a3b8';
    if (v < s.seuil_marche) return '#ef4444';   // sec -> rouge
    if (v < s.seuil_arret) return '#f59e0b';     // moyen -> ambre
    return '#22c55e';                            // humide -> vert
}

// humeur de la plante selon la moyenne
function plantMood(v, s) {
    if (v === null || v === undefined) return { emoji: '🪴', label: '—', ring: '#94a3b8', glow: 'shadow-slate-500/20' };
    if (v < s.seuil_marche) return { emoji: '🥀', label: 'Assoiffée', ring: '#ef4444', glow: 'shadow-red-500/30' };
    if (v < s.seuil_arret) return { emoji: '🌱', label: 'Un peu sèche', ring: '#f59e0b', glow: 'shadow-amber-500/30' };
    if (v < 80) return { emoji: '🪴', label: 'En forme', ring: '#22c55e', glow: 'shadow-green-500/30' };
    return { emoji: '🌿', label: 'Bien arrosée', ring: '#10b981', glow: 'shadow-emerald-500/30' };
}

// ── Jauge circulaire SVG ────────────────────────────────────────────────────
function Ring({ value, color, size = 118, stroke = 10, children }) {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(100, value ?? 0));
    const off = c - (pct / 100) * c;
    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
                    strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
                    style={{ transition: 'stroke-dashoffset .7s ease, stroke .7s ease' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
        </div>
    );
}

export function JeanPaul() {
    const { t } = useTranslation();
    const { token, user } = useAuth();
    const { showToast } = useToast();
    const canOperate = user?.role !== 'viewer';

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);
    const authHeader = useCallback(() => ({ Authorization: `Bearer ${tokenRef.current}` }), []);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await fetch('/api/jeanpaul/status', { headers: authHeader() });
            if (res.ok) {
                const d = await res.json();
                setData(d);
                setForm(prev => prev ?? {
                    seuil_marche: d.settings.seuil_marche,
                    seuil_arret: d.settings.seuil_arret,
                    pluie_seuil_prob: d.settings.pluie_seuil_prob,
                });
            }
        } catch (e) { console.error('jeanpaul status', e); }
    }, [authHeader]);

    useEffect(() => {
        let mounted = true;
        fetchStatus().finally(() => { if (mounted) setLoading(false); });
        const id = setInterval(fetchStatus, POLL_MS);
        return () => { mounted = false; clearInterval(id); };
    }, [fetchStatus]);

    const sendSettings = async (patch, okMsg) => {
        try {
            const res = await fetch('/api/jeanpaul/settings', {
                method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify(patch),
            });
            if (res.ok) { if (okMsg) showToast(okMsg, 'success'); fetchStatus(); }
            else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'Échec', 'error'); }
        } catch { showToast('Erreur réseau', 'error'); }
    };

    const saveThresholds = async () => {
        setSaving(true);
        await sendSettings({ ...form }, 'Réglages enregistrés');
        setSaving(false);
    };

    if (loading) return (
        <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
            <RefreshCw className="w-5 h-5 animate-spin" />Chargement…
        </div>
    );

    const s = data?.settings || {};
    const l = data?.latest || null;
    const online = data?.online;
    const weather = data?.weather || {};
    const rainBlocking = s.respecter_meteo && weather.rain_soon && s.mode === 'auto';
    const moyenne = l ? (l.moyenne ?? ((l.sol1 + l.sol2) / 2)) : null;
    const mood = plantMood(moyenne, s);
    const pumpOn = !!l?.desired_pump;
    const history = (data?.history || []).map(h => ({
        ...h, label: new Date(h.t * 1000).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    }));

    return (
        <div className="space-y-6 pb-10">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <Sprout className="w-7 h-7 text-green-500" />{t('nav.jeanpaul')}
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">Arrosage automatique de la plante</p>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${online ? 'bg-green-500/10 text-green-500' : 'bg-slate-500/10 text-slate-400'}`}>
                    {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
                    {online ? 'En ligne' : 'Hors ligne'}
                </span>
            </div>

            {!l ? (
                <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center">
                    <div className="text-6xl mb-3">🪴</div>
                    <p className="text-foreground font-medium">En attente des données de Jean Paul</p>
                    <p className="text-muted-foreground text-sm mt-1">
                        L'ESP32 doit envoyer sa télémétrie sur <code className="bg-muted px-1 rounded">/api/jeanpaul/telemetry</code>
                    </p>
                </div>
            ) : (
                <>
                    {rainBlocking && (
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-sm text-blue-400 flex items-center gap-2">
                            <CloudRain className="w-4 h-4" />
                            Arrosage suspendu : pluie prévue ({n0(weather.prob, '%')} de probabilité dans les 12 h).
                        </div>
                    )}

                    {/* ── HERO : état de la plante + pompe ── */}
                    <div className={`relative overflow-hidden rounded-2xl border border-border bg-card shadow-lg ${mood.glow}`}>
                        <div className="absolute inset-0 opacity-60"
                            style={{ background: `radial-gradient(120% 90% at 15% 0%, ${mood.ring}22, transparent 60%)` }} />
                        <div className="relative p-6 flex flex-col md:flex-row items-center gap-6">
                            {/* Plante */}
                            <div className="flex items-center gap-5">
                                <div className={`text-7xl leading-none ${pumpOn ? 'animate-bounce' : ''}`}>{mood.emoji}</div>
                                <div>
                                    <p className="text-sm text-muted-foreground">État de la plante</p>
                                    <p className="text-3xl font-extrabold" style={{ color: mood.ring }}>{mood.label}</p>
                                    <p className="text-sm text-muted-foreground mt-1">Humidité moyenne <span className="font-semibold text-foreground">{n0(moyenne, ' %')}</span></p>
                                </div>
                            </div>

                            <div className="md:ml-auto w-full md:w-auto">
                                {/* Pompe */}
                                <div className="flex items-center justify-center gap-3 mb-3">
                                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold
                                        ${pumpOn ? 'bg-sky-500/15 text-sky-500' : 'bg-slate-500/10 text-slate-400'}`}>
                                        <Droplet className={`w-4 h-4 ${pumpOn ? 'animate-pulse fill-sky-500' : ''}`} />
                                        {pumpOn ? 'Arrosage en cours' : 'Pompe à l\'arrêt'}
                                    </div>
                                </div>
                                {canOperate && (
                                    <div className="flex gap-2 justify-center">
                                        <button onClick={() => sendSettings({ mode: 'manual', manual_pump: true }, 'Arrosage lancé')}
                                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 active:scale-95 transition-all shadow-md shadow-sky-500/30">
                                            <Play className="w-4 h-4" />Arroser
                                        </button>
                                        <button onClick={() => sendSettings({ mode: 'manual', manual_pump: false }, 'Arrosage arrêté')}
                                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-muted text-foreground text-sm font-semibold hover:bg-muted/70 active:scale-95 transition-all">
                                            <Square className="w-4 h-4" />Stop
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Jauges sondes + air + batterie ── */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-card border border-border rounded-2xl p-4 flex flex-col items-center">
                            <Ring value={l.sol1} color={moistColor(l.sol1, s)}>
                                <span className="text-2xl font-bold text-foreground tabular-nums">{n0(l.sol1)}<span className="text-sm text-muted-foreground">%</span></span>
                            </Ring>
                            <p className="mt-2 text-sm font-medium text-muted-foreground flex items-center gap-1"><Droplets className="w-4 h-4" />Sol 1</p>
                        </div>
                        <div className="bg-card border border-border rounded-2xl p-4 flex flex-col items-center">
                            <Ring value={l.sol2} color={moistColor(l.sol2, s)}>
                                <span className="text-2xl font-bold text-foreground tabular-nums">{n0(l.sol2)}<span className="text-sm text-muted-foreground">%</span></span>
                            </Ring>
                            <p className="mt-2 text-sm font-medium text-muted-foreground flex items-center gap-1"><Droplets className="w-4 h-4" />Sol 2</p>
                        </div>
                        <div className="bg-card border border-border rounded-2xl p-4 flex flex-col items-center justify-center">
                            <Thermometer className="w-6 h-6 text-orange-400 mb-1" />
                            <span className="text-3xl font-bold text-foreground tabular-nums">{n1(l.temperature, '°')}</span>
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Wind className="w-3 h-3" />Air · {n0(l.humiditeAir, '%')} hum.</p>
                        </div>
                        <div className="bg-card border border-border rounded-2xl p-4 flex flex-col items-center justify-center">
                            <BatteryMedium className={`w-6 h-6 mb-1 ${(l.batteriePct ?? 0) < 20 ? 'text-red-500' : 'text-green-500'}`} />
                            <span className="text-3xl font-bold text-foreground tabular-nums">{n1(l.batterie, ' V')}</span>
                            <div className="w-full mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${(l.batteriePct ?? 0) < 20 ? 'bg-red-500' : 'bg-green-500'}`} style={{ width: `${Math.max(0, Math.min(100, l.batteriePct ?? 0))}%` }} />
                            </div>
                        </div>
                    </div>

                    {/* ── Mode + réglages ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-card border border-border rounded-2xl p-5">
                            <span className="font-semibold text-foreground flex items-center gap-2 mb-4"><Gauge className="w-4 h-4 text-primary" />Mode</span>
                            <div className="flex gap-2">
                                {['auto', 'manual'].map(m => (
                                    <button key={m} disabled={!canOperate}
                                        onClick={() => sendSettings({ mode: m }, `Mode ${m === 'auto' ? 'automatique' : 'manuel'}`)}
                                        className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50
                                            ${s.mode === m ? 'bg-primary text-primary-foreground shadow-md' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                                        {m === 'auto' ? '🤖 Automatique' : '✋ Manuel'}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-3">
                                En auto : arrose sous <strong style={{ color: '#ef4444' }}>{s.seuil_marche}%</strong>, arrête à <strong style={{ color: '#22c55e' }}>{s.seuil_arret}%</strong>.
                            </p>
                            <label className="flex items-center gap-2 mt-4 cursor-pointer select-none">
                                <div className={`relative w-10 h-6 rounded-full transition-colors ${s.respecter_meteo ? 'bg-primary' : 'bg-muted'}`}
                                    onClick={() => canOperate && sendSettings({ respecter_meteo: !s.respecter_meteo })}>
                                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${s.respecter_meteo ? 'left-5' : 'left-1'}`} />
                                </div>
                                <span className="text-sm text-foreground flex items-center gap-1"><CloudRain className="w-4 h-4 text-blue-400" />Ne pas arroser si pluie prévue</span>
                            </label>
                        </div>

                        <div className="bg-card border border-border rounded-2xl p-5">
                            <span className="font-semibold text-foreground flex items-center gap-2 mb-4"><Droplets className="w-4 h-4 text-primary" />Seuils d'arrosage</span>
                            <div className="grid grid-cols-3 gap-3">
                                {[['seuil_marche', 'Marche', '#ef4444'], ['seuil_arret', 'Arrêt', '#22c55e'], ['pluie_seuil_prob', 'Pluie', '#3b82f6']].map(([k, lbl, col]) => (
                                    <div key={k}>
                                        <label className="block text-xs text-muted-foreground mb-1.5" style={{ color: col }}>{lbl} (%)</label>
                                        <input type="number" min="0" max="100" disabled={!canOperate} value={form?.[k] ?? ''}
                                            onChange={e => setForm(f => ({ ...f, [k]: Number(e.target.value) }))}
                                            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50" />
                                    </div>
                                ))}
                            </div>
                            {canOperate && (
                                <button onClick={saveThresholds} disabled={saving}
                                    className="mt-4 w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
                                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ── Historique en dégradé avec zones de couleur ── */}
                    {history.length > 1 && (
                        <div className="bg-card border border-border rounded-2xl p-5">
                            <span className="font-semibold text-foreground mb-1 block">Historique de l'humidité</span>
                            <p className="text-xs text-muted-foreground mb-3">Zones : 🔴 sèche · 🟠 correcte · 🟢 bien arrosée</p>
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart data={history} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="jpFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#22c55e" stopOpacity={0.5} />
                                            <stop offset="100%" stopColor="#22c55e" stopOpacity={0.03} />
                                        </linearGradient>
                                    </defs>
                                    {/* zones de couleur (bandes) */}
                                    <ReferenceArea y1={0} y2={s.seuil_marche} fill="#ef4444" fillOpacity={0.06} />
                                    <ReferenceArea y1={s.seuil_marche} y2={s.seuil_arret} fill="#f59e0b" fillOpacity={0.06} />
                                    <ReferenceArea y1={s.seuil_arret} y2={100} fill="#22c55e" fillOpacity={0.06} />
                                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} interval="preserveStartEnd" minTickGap={40} />
                                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} tickFormatter={v => `${v}%`} />
                                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.5rem', fontSize: 12 }}
                                        formatter={(v, name) => [`${Number(v).toFixed(1)}%`, name === 'moyenne' ? 'Moyenne' : name]} />
                                    <Area type="monotone" dataKey="moyenne" stroke="#16a34a" strokeWidth={2.5} fill="url(#jpFill)" dot={false} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
